import { useCallback, useEffect, useState } from 'react';
import { LockClosedIcon, XMarkIcon } from '@heroicons/react/24/solid';
import useEmblaCarousel from 'embla-carousel-react';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from 'react-responsive';

import useAccountQuery from 'api/queries/useAccountQuery';
import useReferralCreditsQuery from 'api/queries/useReferralCreditsQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import { BOYSENBERRY_S3_IMAGES } from 'constants';
import { Drawer } from 'honeydew/Drawer';
import { AppleAppStoreBadge, GooglePlayBadge } from 'honeydew/icons/AppStores';
import Modal from 'honeydew/Modal';
import useBrandLinks from 'hooks/useBrandLinks';
import useGlobalStore from 'hooks/useGlobalStore';
import useTailwindTheme from 'hooks/useTailwindTheme';
import CopyReferralCode from 'shared/CopyReferralCode';
import { trackEvent } from 'utils/analyticsUtils';
import { getDay, localizeTimeZone } from 'utils/dateUtils';
import { isAndroid, isIOS } from 'utils/globalUtils';

import useOnboardingModals from './useOnboardingModals';

function OnboardingCard({ children }) {
    return (
        <div className="h-full rounded-xl border border-black bg-white p-15 md:p-20">
            <div className="flex h-full flex-col items-center text-center">{children}</div>
        </div>
    );
}

function ClosedWindowOnboardingModal() {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const {
        closedWindowOnboardingModalOpen,
        isMisfitsTheme,
        setClosedWindowOnboardingModalOpen,
        setOpenWindowOnboardingModalOpen,
    } = useGlobalStore();
    const { appStore, googlePlayStore } = useBrandLinks();

    const { closeClosedWindowModal, isOnboardingOptimizationEnabled } = useOnboardingModals({
        setClosedWindowOnboardingModalOpen,
        setOpenWindowOnboardingModalOpen,
    });

    const { data: account } = useAccountQuery();
    const { data: subscription } = useSubscriptionQuery();
    const { data: referrals } = useReferralCreditsQuery();

    const { marketCloseDate, marketOpenDate } = account ?? {};
    const { newCustValue, referrerValue } = referrals?.referralRewards ?? {};

    const openDay = marketOpenDate?.date ? getDay(marketOpenDate.date, true) : null;
    const openTime = marketOpenDate?.date ? localizeTimeZone(marketOpenDate.date, true)?.replace(':00', '') : null;

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'center' });

    const referralLink = subscription?.vanityReferralCode
        ? `https://www.${isMisfitsTheme ? 'misfitsmarket' : 'imperfectfoods'}.com/?promo=${subscription.vanityReferralCode}`
        : '';

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return undefined;
        onSelect();
        emblaApi.on('select', onSelect);
        return () => {
            emblaApi.off('select', onSelect);
        };
    }, [emblaApi, onSelect]);

    useEffect(() => {
        if (closedWindowOnboardingModalOpen) {
            trackEvent('closed window onboarding modal viewed', { slide_number: selectedIndex + 1 });
        }
    }, [closedWindowOnboardingModalOpen, selectedIndex]);

    if (!isOnboardingOptimizationEnabled) {
        return null;
    }

    const handleClose = () => {
        closeClosedWindowModal();
    };

    const appNotificationsCard = (
        <OnboardingCard>
            <img
                src={`${BOYSENBERRY_S3_IMAGES}onboarding-app-icon.svg`}
                alt="Notifications"
                className="h-90 w-90 mb-10"
            />
            <h3 className="text-body-md mb-10 font-grotesk-bold">
                Never miss a shopping window with app notifications
            </h3>
            <p className="text-body-mobile-xs md:text-body-desktop-sm">
                Download the {isMisfitsTheme ? 'Misfits Market' : 'Imperfect Foods'} app and enable notifications to be
                reminded when to shop.
            </p>
            {mobile && !window.isNativeApp && (
                <div className="mt-10">
                    {isIOS() && (
                        <a
                            href={appStore}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() =>
                                trackEvent('closed window onboarding cta click', { cta_label: 'apple app store' })
                            }
                        >
                            <AppleAppStoreBadge width={140} />
                        </a>
                    )}
                    {isAndroid() && (
                        <a
                            href={googlePlayStore}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() =>
                                trackEvent('closed window onboarding cta click', { cta_label: 'google play store' })
                            }
                        >
                            <GooglePlayBadge width={160} />
                        </a>
                    )}
                </div>
            )}
            {!mobile && (
                <p className="text-body-sm mt-auto text-grey">
                    Available in the Apple App Store and Google Play Store.
                </p>
            )}
        </OnboardingCard>
    );

    const calendarReminderCard = (
        <OnboardingCard>
            <Helmet>
                <script type="text/javascript" src="https://addevent.com/libs/atc/1.6.1/atc.min.js" async defer />
            </Helmet>
            <img
                src={`${BOYSENBERRY_S3_IMAGES}onboarding-calendar-icon.svg`}
                alt="Notifications"
                className="h-90 w-90 mb-10"
            />
            <h3 className="text-body-md mb-10 font-grotesk-bold">Add a calendar reminder</h3>
            <p className="text-body-mobile-xs md:text-body-desktop-sm">
                Click the button below to add a shopping reminder to your calendar of choice.
            </p>
            <div className="mt-auto w-full">
                {marketOpenDate?.date && marketCloseDate?.date && (
                    <>
                        <button
                            title="Add to Calendar"
                            className="addeventatc !z-10 mx-auto flex h-[44px] w-full max-w-[200px] !items-center !justify-center !rounded-md !border-2 !border-black !bg-white !px-0 !pt-[8px] transition-all hover:!bg-off-white"
                            data-intel="false"
                            data-google-api="true"
                            data-dropdown-y="up"
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                trackEvent('closed window onboarding cta click', { cta_label: 'add to calendar' });
                            }}
                        >
                            <span className="text-body-sm font-grotesk-bold">Add to Calendar</span>
                            <span className="start">{marketOpenDate.date}</span>
                            <span className="end">{marketCloseDate.date}</span>
                            <span className="timezone">{marketOpenDate.timezone}</span>
                            <span className="title">
                                Build your {isMisfitsTheme ? 'Misfits Market' : 'Imperfect Foods'} box
                            </span>
                            <span className="description">
                                Your window is open to customize and add discounted grocery goods to your order.
                            </span>
                            <span className="location">
                                {isMisfitsTheme ? 'https://www.misfitsmarket.com' : 'https://www.imperfectfoods.com/'}
                            </span>
                            <span className="recurring">&lsquo;FREQ=WEEKLY;&rsquo;</span>
                        </button>
                        <div className="mt-10 flex justify-center">
                            <div className="h-6 w-6 rounded-full bg-kale" />
                        </div>
                    </>
                )}
            </div>
        </OnboardingCard>
    );

    const referFriendCard = (
        <OnboardingCard>
            <img
                src={`${BOYSENBERRY_S3_IMAGES}onboarding-box-icon.svg`}
                alt="Notifications"
                className="h-90 w-90 mb-10"
            />
            <h3 className="text-body-md mb-10 font-grotesk-bold">Refer a friend</h3>
            <p className="text-body-mobile-xs md:text-body-desktop-sm">
                Get a ${referrerValue || 15} credit to spend on groceries for every friend that you refer (plus,
                they&apos;ll get ${newCustValue || 20}!).
            </p>
            <div className="mt-auto flex w-full gap-10 [&>input]:h-[44px]">
                <CopyReferralCode
                    link={referralLink}
                    buttonCopy="Copy Link"
                    buttonSize="md"
                    onCopy={() => trackEvent('closed window onboarding cta click', { cta_label: 'copy referral link' })}
                />
            </div>
        </OnboardingCard>
    );

    const cards = [
        { key: 'app-notifications', content: appNotificationsCard },
        { key: 'calendar-reminder', content: calendarReminderCard },
        { key: 'refer-friend', content: referFriendCard },
    ];

    const content = (
        <div className="p-20 md:p-0">
            <h2 className="text-heading-xs mb-15 text-center font-grotesk-bold">
                Welcome{subscription?.shippingAddr?.firstName ? `, ${subscription.shippingAddr.firstName}` : ''}!
            </h2>

            <div className="mx-auto mb-15 max-w-[500px] rounded-lg bg-mango p-5 text-center md:mb-20">
                <div className="flex items-center justify-center gap-[5px]">
                    <LockClosedIcon style={{ width: 16, height: 16 }} />
                    <span className="font-grotesk-bold text-body-mobile-xs uppercase tracking-wide md:text-body-desktop-sm">
                        This week&apos;s shop isn&apos;t open quite yet
                    </span>
                </div>
                {openDay && openTime && (
                    <p className="text-body-mobile-xs md:text-body-desktop-sm">
                        Shopping starts{' '}
                        <span className="font-grotesk-bold">
                            every {openDay} at {openTime}
                        </span>
                        .
                    </p>
                )}
            </div>

            <p className="text-body-md mb-15 text-center md:mb-20">What you can do to prepare:</p>

            {mobile ? (
                <>
                    <div className="-mx-20 overflow-hidden" ref={emblaRef}>
                        <div className="flex">
                            {cards.map((card, index) => (
                                <div
                                    key={card.key}
                                    className={`min-w-0 flex-[0_0_calc(100%-30px)] px-5 ${
                                        index === 0 ? 'pl-30' : ''
                                    } ${index === cards.length - 1 ? 'pr-30' : ''}`}
                                >
                                    {card.content}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mt-20 flex justify-center gap-10">
                        {cards.map((card, index) => (
                            <button
                                key={card.key}
                                type="button"
                                className={`h-[7px] w-[7px] rounded-full transition-colors ${
                                    index === selectedIndex ? 'bg-black' : 'bg-grey-light'
                                }`}
                                onClick={() => emblaApi?.scrollTo(index)}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </>
            ) : (
                <div className="grid grid-cols-3 gap-15">
                    {cards.map((card) => (
                        <div key={card.key}>{card.content}</div>
                    ))}
                </div>
            )}
        </div>
    );

    if (mobile) {
        return (
            <Drawer
                open={closedWindowOnboardingModalOpen}
                toggleDrawer={handleClose}
                anchor="bottom"
                sx={{
                    '& .MuiDrawer-paper': {
                        borderTopLeftRadius: '16px',
                        borderTopRightRadius: '16px',
                        backgroundColor: '#F5F0E8',
                    },
                }}
            >
                <div className="relative">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute right-15 top-15 z-10"
                        aria-label="Close drawer"
                    >
                        <XMarkIcon className="h-25 w-25 text-black" />
                    </button>
                    {content}
                </div>
            </Drawer>
        );
    }

    return (
        <Modal
            open={closedWindowOnboardingModalOpen}
            onClose={handleClose}
            bgColor="bg-malt"
            style={{ outline: 'none' }}
            styles={{
                contentRoot:
                    'md:max-w-[1000px] lg:max-w-[1045px] !rounded-2xl p-30 !outline-none focus:!outline-none focus-visible:!outline-none [&>button]:bg-transparent !overflow-visible bg-[#F5F0E8]',
            }}
        >
            {content}
        </Modal>
    );
}

export default ClosedWindowOnboardingModal;
